import React, { useState, useEffect, useCallback } from 'react';
import {
  Sun, Moon, LogOut, Users, LayoutDashboard,
  Settings, HelpCircle, Menu, X, Search, Edit3,
  History, Mail, Send, Check, AlertTriangle, Trash2,
  Plus, Eye, Clock, ChevronDown, ChevronRight,
  BarChart3, MousePointerClick, Inbox, FileText,
  RefreshCw, Filter, ArrowUpRight, Zap, Activity
} from 'lucide-react';
import { api } from '../api';
import './DashboardPage.css';

const LOGO_URL = 'https://acesso.ihubs.app/api/uploads/logo/acesso/logo-light-1773085624457.png';

function EmailPage({ user, onLogout, theme, setTheme, setPage }) {
  const initials = user.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '??';
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Active section
  const [section, setSection] = useState('campaigns'); // campaigns, compose, templates, stats

  // Data
  const [users, setUsers] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [emailLogs, setEmailLogs] = useState({ logs: [], total: 0 });
  const [emailLogsPage, setEmailLogsPage] = useState(1);
  const [emailLogsFilter, setEmailLogsFilter] = useState('');
  const [emailStats, setEmailStats] = useState(null);
  const [noDepositUsers, setNoDepositUsers] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [selectedLogClicks, setSelectedLogClicks] = useState([]);

  // Compose form
  const [composeForm, setComposeForm] = useState({
    mode: 'individual', subject: '', body_html: '', template_id: null,
    user_ids: [], user_id: ''
  });

  // Template form
  const [templateForm, setTemplateForm] = useState({
    name: '', subject: '', body_html: '', category: 'geral', editing: null
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchLogs, setSearchLogs] = useState('');

  // Load data
  const loadUsers = useCallback(async () => {
    try { const r = await api.getUsers(); setUsers(r); } catch {}
  }, []);

  const loadTemplates = useCallback(async () => {
    try { const r = await api.getEmailTemplates(); setEmailTemplates(r); } catch {}
  }, []);

  const loadLogs = useCallback(async (page = 1, status = '') => {
    try {
      const params = { page, limit: 25 };
      if (status) params.status = status;
      const r = await api.getEmailLogs(params);
      setEmailLogs(r);
    } catch {}
  }, []);

  const loadStats = useCallback(async () => {
    try { const r = await api.getEmailStats(); setEmailStats(r); } catch {}
  }, []);

  const loadNoDepositUsers = useCallback(async () => {
    try { const r = await api.getNoDepositUsers(); setNoDepositUsers(r); } catch {}
  }, []);

  const loadLogClicks = async (logId) => {
    try { const r = await api.getEmailClicks(logId); setSelectedLogClicks(r); } catch {}
  };

  useEffect(() => {
    loadUsers();
    loadLogs(1, '');
    loadStats();
  }, [loadUsers, loadLogs, loadStats]);

  // Send email
  const handleSend = async () => {
    setError(''); setSuccess(''); setLoading(true);
    try {
      if (composeForm.mode === 'reminder') {
        const r = await api.sendReminders({
          subject: composeForm.subject || undefined,
          body_html: composeForm.body_html || undefined,
          template_id: composeForm.template_id || undefined
        });
        setSuccess(`Lembretes enviados: ${r.sent}, Falhas: ${r.failed}`);
      } else if (composeForm.mode === 'bulk') {
        if (!composeForm.user_ids.length) { setError('Selecione ao menos um destinatario'); setLoading(false); return; }
        const r = await api.sendBulkEmail({
          user_ids: composeForm.user_ids, subject: composeForm.subject,
          body_html: composeForm.body_html, template_id: composeForm.template_id || undefined
        });
        setSuccess(`Enviados: ${r.sent}, Falhas: ${r.failed}`);
      } else {
        if (!composeForm.user_id) { setError('Selecione um destinatario'); setLoading(false); return; }
        if (!composeForm.subject || !composeForm.body_html) { setError('Assunto e corpo sao obrigatorios'); setLoading(false); return; }
        await api.sendEmail({
          user_id: parseInt(composeForm.user_id), subject: composeForm.subject,
          body_html: composeForm.body_html, template_id: composeForm.template_id || undefined
        });
        setSuccess('E-mail enviado com sucesso!');
      }
      setComposeForm({ mode: composeForm.mode, subject: '', body_html: '', template_id: null, user_ids: [], user_id: '' });
      loadLogs(1, emailLogsFilter);
      loadStats();
    } catch (err) {
      setError(err.error || 'Erro ao enviar e-mail');
    }
    setLoading(false);
  };

  // Save template
  const handleSaveTemplate = async () => {
    setError(''); setLoading(true);
    try {
      if (templateForm.editing) {
        await api.updateEmailTemplate(templateForm.editing, templateForm);
        setSuccess('Template atualizado');
      } else {
        await api.createEmailTemplate(templateForm);
        setSuccess('Template criado');
      }
      setTemplateForm({ name: '', subject: '', body_html: '', category: 'geral', editing: null });
      loadTemplates();
    } catch (err) { setError(err.error || 'Erro ao salvar template'); }
    setLoading(false);
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Excluir este template?')) return;
    try { await api.deleteEmailTemplate(id); loadTemplates(); } catch {}
  };

  // Status helpers
  const statusConfig = {
    clicked: { bg: 'rgba(59,130,246,0.1)', color: '#3b82f6', label: 'Clicou', icon: MousePointerClick },
    opened: { bg: 'rgba(34,197,94,0.1)', color: '#22c55e', label: 'Aberto', icon: Eye },
    failed: { bg: 'rgba(239,68,68,0.1)', color: '#ef4444', label: 'Falha', icon: AlertTriangle },
    sent: { bg: 'rgba(245,158,11,0.1)', color: '#f59e0b', label: 'Enviado', icon: Send }
  };

  const StatusBadge = ({ status, clickCount }) => {
    const st = statusConfig[status] || statusConfig.sent;
    return (
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
        {clickCount > 0 && (
          <span style={{ padding: '3px 10px', borderRadius: '6px', fontSize: '0.7rem', fontWeight: 600, background: 'rgba(59,130,246,0.1)', color: '#3b82f6' }}>
            {clickCount} clique{clickCount > 1 ? 's' : ''}
          </span>
        )}
        <span style={{ padding: '3px 10px', borderRadius: '6px', fontSize: '0.7rem', fontWeight: 600, background: st.bg, color: st.color, display: 'flex', alignItems: 'center', gap: '4px' }}>
          <st.icon size={11} /> {st.label}
        </span>
      </div>
    );
  };

  const clientUsers = users.filter(u => u.role === 'usuario');
  const usersWithEmail = clientUsers.filter(u => u.email && u.email.trim());

  // Filter logs by search
  const filteredLogs = emailLogs.logs.filter(log => {
    if (!searchLogs) return true;
    const q = searchLogs.toLowerCase();
    return (log.user_name || '').toLowerCase().includes(q) ||
      (log.user_email || '').toLowerCase().includes(q) ||
      (log.subject || '').toLowerCase().includes(q);
  });

  return (
    <div className={`dashboard-container ${theme}`}>
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-top">
          <div className="sidebar-logo">
            <img src={LOGO_URL} alt="Logo" className="sidebar-logo-img" />
          </div>
          <nav className="sidebar-nav">
            <a href="#dashboard" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('dashboard'); }}>
              <LayoutDashboard size={19} /><span>Dashboard</span>
            </a>
            <a href="#admin" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('admin'); }}>
              <Users size={19} /><span>Admin</span>
            </a>
            <a href="#emails" className="nav-item active" onClick={() => setSidebarOpen(false)}>
              <Mail size={19} /><span>E-mails</span>
            </a>
            <a href="#automation" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('automation'); }}>
              <Zap size={19} /><span>Automacao IA</span>
            </a>
            <a href="#activity" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('activity'); }}>
              <Activity size={19} /><span>Atividade</span>
            </a>
            <a href="#history" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('history'); }}>
              <History size={19} /><span>Historico</span>
            </a>
            <a href="#settings" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('settings'); }}>
              <Settings size={19} /><span>Configuracoes</span>
            </a>
            <a href="#help" className="nav-item">
              <HelpCircle size={19} /><span>Ajuda</span>
            </a>
          </nav>
        </div>
        <div className="sidebar-bottom">
          <button className="sidebar-theme-btn" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            <span>{theme === 'dark' ? 'Tema claro' : 'Tema escuro'}</span>
          </button>
          <div className="sidebar-user">
            <div className="user-avatar">{initials}</div>
            <div className="user-info">
              <span className="user-name">{user.name}</span>
              <span className="user-role">{user.role}</span>
            </div>
            <button className="logout-btn-sidebar" onClick={onLogout} title="Sair"><LogOut size={16} /></button>
          </div>
        </div>
      </aside>

      <div className="main-wrapper">
        <header className="topbar">
          <button className="menu-btn" onClick={() => setSidebarOpen(true)}><Menu size={20} /></button>
          <img src={LOGO_URL} alt="Logo" className="topbar-logo" />
          <div className="topbar-right">
            <button className="topbar-theme" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <div className="user-avatar small">{initials}</div>
          </div>
        </header>

        <main className="dash-main" style={{ maxWidth: '1100px' }}>
          {/* Page Header */}
          <div className="page-header">
            <div>
              <p className="page-greeting">Central de E-mails</p>
              <h1 className="page-title">Campanhas & Comunicacao</h1>
            </div>
          </div>

          {/* Section Navigation */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: '28px', borderBottom: '1px solid var(--card-border)', paddingBottom: '0' }}>
            {[
              ['campaigns', 'Campanhas', Inbox],
              ['compose', 'Compor', Send],
              ['templates', 'Templates', FileText],
              ['stats', 'Estatisticas', BarChart3]
            ].map(([key, label, Icon]) => (
              <button key={key} onClick={() => {
                setSection(key); setError(''); setSuccess('');
                if (key === 'campaigns') loadLogs(1, emailLogsFilter);
                if (key === 'templates') loadTemplates();
                if (key === 'stats') loadStats();
                if (key === 'compose') { loadTemplates(); loadNoDepositUsers(); }
              }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '7px',
                  padding: '12px 20px', border: 'none', background: 'transparent', cursor: 'pointer',
                  fontSize: '0.84rem', fontWeight: section === key ? 600 : 400,
                  color: section === key ? '#3b82f6' : 'var(--text-color)',
                  borderBottom: section === key ? '2px solid #3b82f6' : '2px solid transparent',
                  transition: 'all 0.2s'
                }}>
                <Icon size={16} /> {label}
              </button>
            ))}
          </div>

          {/* Alerts */}
          {error && (
            <div style={{ padding: '12px 16px', borderRadius: '10px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#ef4444', fontSize: '0.82rem', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <AlertTriangle size={15} /> {error}
            </div>
          )}
          {success && (
            <div style={{ padding: '12px 16px', borderRadius: '10px', background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)', color: '#22c55e', fontSize: '0.82rem', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Check size={15} /> {success}
            </div>
          )}

          {/* ================================ */}
          {/* CAMPAIGNS / HISTORY SECTION      */}
          {/* ================================ */}
          {section === 'campaigns' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

              {/* Quick Stats Bar */}
              {emailStats && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px' }}>
                  {[
                    { label: 'Total enviados', value: emailStats.summary.total, color: 'var(--text-color)', icon: Send },
                    { label: 'Abertos', value: parseInt(emailStats.summary.opened || 0) + parseInt(emailStats.summary.clicked || 0), color: '#22c55e', icon: Eye },
                    { label: 'Clicaram', value: emailStats.summary.clicked || 0, color: '#3b82f6', icon: MousePointerClick },
                    { label: 'Taxa abertura', value: `${emailStats.summary.open_rate || 0}%`, color: '#8b5cf6', icon: BarChart3 },
                    { label: 'Taxa cliques', value: `${emailStats.summary.click_rate || 0}%`, color: '#f59e0b', icon: ArrowUpRight },
                  ].map((item, i) => (
                    <div key={i} style={{
                      padding: '16px', borderRadius: '12px',
                      background: 'var(--card-bg)', border: '1px solid var(--card-border)',
                      display: 'flex', flexDirection: 'column', gap: '6px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-muted)', fontSize: '0.72rem', fontWeight: 500 }}>
                        <item.icon size={13} /> {item.label}
                      </div>
                      <div style={{ fontSize: '1.3rem', fontWeight: 700, color: item.color }}>{item.value}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Filters + Search */}
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ position: 'relative', flex: '1 1 250px' }}>
                  <Search size={15} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                  <input
                    type="text" placeholder="Buscar por nome, email ou assunto..."
                    value={searchLogs} onChange={e => setSearchLogs(e.target.value)}
                    style={{
                      width: '100%', padding: '10px 12px 10px 36px', borderRadius: '10px',
                      border: '1px solid var(--card-border)', background: 'var(--input-bg)',
                      color: 'var(--input-text)', fontSize: '0.82rem', outline: 'none'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {[['', 'Todos'], ['sent', 'Enviados'], ['opened', 'Abertos'], ['clicked', 'Clicados'], ['failed', 'Falhas']].map(([key, label]) => (
                    <button key={key} onClick={() => { setEmailLogsFilter(key); setEmailLogsPage(1); loadLogs(1, key); }}
                      style={{
                        padding: '8px 14px', borderRadius: '8px', fontSize: '0.76rem', cursor: 'pointer',
                        border: emailLogsFilter === key ? '2px solid #3b82f6' : '1px solid var(--card-border)',
                        background: emailLogsFilter === key ? 'rgba(59,130,246,0.08)' : 'transparent',
                        color: emailLogsFilter === key ? '#3b82f6' : 'var(--text-color)',
                        fontWeight: emailLogsFilter === key ? 600 : 400, transition: 'all 0.2s'
                      }}>{label}</button>
                  ))}
                </div>
                <button onClick={() => { loadLogs(emailLogsPage, emailLogsFilter); loadStats(); }}
                  style={{ padding: '8px', borderRadius: '8px', border: '1px solid var(--card-border)', background: 'transparent', cursor: 'pointer', color: 'var(--text-color)', display: 'flex' }}
                  title="Atualizar">
                  <RefreshCw size={15} />
                </button>
              </div>

              {/* Email List - Table Style */}
              <div style={{ background: 'var(--card-bg)', borderRadius: '14px', border: '1px solid var(--card-border)', overflow: 'hidden' }}>
                {/* Table Header */}
                <div style={{
                  display: 'grid', gridTemplateColumns: '1.5fr 2fr 1fr 1fr',
                  gap: '12px', padding: '14px 20px',
                  borderBottom: '1px solid var(--card-border)',
                  fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px'
                }}>
                  <span>Destinatario</span>
                  <span>Assunto</span>
                  <span>Status</span>
                  <span>Data</span>
                </div>

                {/* Table Rows */}
                {filteredLogs.length === 0 ? (
                  <div style={{ padding: '48px 20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                    <Mail size={32} style={{ opacity: 0.3, marginBottom: '12px' }} />
                    <p>Nenhum e-mail encontrado</p>
                  </div>
                ) : filteredLogs.map(log => (
                  <div key={log.id}
                    onClick={() => { setSelectedLog(selectedLog?.id === log.id ? null : log); if (selectedLog?.id !== log.id) loadLogClicks(log.id); }}
                    style={{
                      display: 'grid', gridTemplateColumns: '1.5fr 2fr 1fr 1fr',
                      gap: '12px', padding: '14px 20px', alignItems: 'center',
                      borderBottom: '1px solid var(--card-border)',
                      cursor: 'pointer', transition: 'background 0.15s',
                      background: selectedLog?.id === log.id ? 'rgba(59,130,246,0.04)' : 'transparent'
                    }}
                    onMouseEnter={e => { if (selectedLog?.id !== log.id) e.currentTarget.style.background = 'var(--card-hover)'; }}
                    onMouseLeave={e => { if (selectedLog?.id !== log.id) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <div>
                      <div style={{ fontSize: '0.84rem', fontWeight: 500, color: 'var(--title-color)' }}>{log.user_name || 'Removido'}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '2px' }}>{log.user_email || ''}</div>
                    </div>
                    <div style={{ fontSize: '0.82rem', color: 'var(--text-color)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {log.subject}
                    </div>
                    <StatusBadge status={log.status} clickCount={log.click_count} />
                    <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>
                      {new Date(log.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Selected Log Detail */}
              {selectedLog && (
                <div style={{
                  background: 'var(--card-bg)', borderRadius: '14px', border: '1px solid var(--card-border)',
                  padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <h3 style={{ margin: '0 0 4px', fontSize: '1rem', color: 'var(--title-color)', fontWeight: 600 }}>{selectedLog.subject}</h3>
                      <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                        Para: {selectedLog.user_name} ({selectedLog.user_email})
                      </p>
                    </div>
                    <button onClick={() => setSelectedLog(null)}
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '4px' }}>
                      <X size={18} />
                    </button>
                  </div>

                  {/* Timeline */}
                  <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', fontSize: '0.78rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-color)' }}>
                      <Send size={13} /> Enviado em {new Date(selectedLog.created_at).toLocaleString('pt-BR')}
                    </div>
                    {selectedLog.opened_at && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#22c55e' }}>
                        <Eye size={13} /> Aberto em {new Date(selectedLog.opened_at).toLocaleString('pt-BR')}
                      </div>
                    )}
                    {selectedLog.clicked_at && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#3b82f6' }}>
                        <MousePointerClick size={13} /> Primeiro clique em {new Date(selectedLog.clicked_at).toLocaleString('pt-BR')}
                      </div>
                    )}
                  </div>

                  {/* Email Body Preview - Full render como o cliente recebe */}
                  {selectedLog.id && (
                    <div>
                      <h4 style={{ margin: '0 0 10px', fontSize: '0.84rem', fontWeight: 600, color: 'var(--title-color)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Eye size={14} /> Visualizacao do E-mail (como o cliente recebe)
                      </h4>
                      <div style={{
                        borderRadius: '12px', border: '1px solid var(--card-border)',
                        overflow: 'hidden', background: '#f0f2f5',
                      }}>
                        <iframe
                          title="Email Preview"
                          src={`https://app.solvere.pro/api/email/preview/${selectedLog.id}`}
                          style={{
                            width: '100%', height: '600px', border: 'none',
                            background: '#f0f2f5',
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Click details */}
                  {selectedLogClicks.length > 0 && (
                    <div>
                      <h4 style={{ margin: '0 0 10px', fontSize: '0.84rem', fontWeight: 600, color: 'var(--title-color)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <MousePointerClick size={14} /> Cliques registrados ({selectedLogClicks.length})
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {selectedLogClicks.map((click, i) => (
                          <div key={i} style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--card-border)',
                            fontSize: '0.76rem'
                          }}>
                            <span style={{ color: '#3b82f6', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '60%' }}>
                              {click.original_url}
                            </span>
                            <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
                              {new Date(click.clicked_at).toLocaleString('pt-BR')}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Pagination */}
              {emailLogs.total > 25 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', paddingTop: '8px' }}>
                  <button disabled={emailLogsPage <= 1}
                    onClick={() => { const p = emailLogsPage - 1; setEmailLogsPage(p); loadLogs(p, emailLogsFilter); }}
                    style={{
                      padding: '8px 16px', borderRadius: '8px', border: '1px solid var(--card-border)',
                      background: 'transparent', cursor: 'pointer', color: 'var(--text-color)',
                      opacity: emailLogsPage <= 1 ? 0.4 : 1, fontSize: '0.82rem'
                    }}>Anterior</button>
                  <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                    Pagina {emailLogsPage} de {Math.ceil(emailLogs.total / 25)}
                  </span>
                  <button disabled={emailLogsPage >= Math.ceil(emailLogs.total / 25)}
                    onClick={() => { const p = emailLogsPage + 1; setEmailLogsPage(p); loadLogs(p, emailLogsFilter); }}
                    style={{
                      padding: '8px 16px', borderRadius: '8px', border: '1px solid var(--card-border)',
                      background: 'transparent', cursor: 'pointer', color: 'var(--text-color)',
                      opacity: emailLogsPage >= Math.ceil(emailLogs.total / 25) ? 0.4 : 1, fontSize: '0.82rem'
                    }}>Proxima</button>
                </div>
              )}
            </div>
          )}

          {/* ================================ */}
          {/* COMPOSE SECTION                  */}
          {/* ================================ */}
          {section === 'compose' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '24px' }}>
              {/* Main compose area */}
              <div style={{
                background: 'var(--card-bg)', borderRadius: '14px', border: '1px solid var(--card-border)',
                padding: '28px', display: 'flex', flexDirection: 'column', gap: '20px'
              }}>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: 'var(--title-color)' }}>Compor e-mail</h3>

                {/* Mode selector */}
                <div>
                  <label style={{ fontSize: '0.76rem', fontWeight: 500, color: 'var(--text-muted)', marginBottom: '8px', display: 'block' }}>Tipo de envio</label>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {[['individual', 'Individual', '1 destinatario'], ['bulk', 'Em massa', 'Varios destinatarios'], ['reminder', 'Lembrete', 'Quem nao depositou']].map(([key, label, desc]) => (
                      <button key={key} onClick={() => setComposeForm(f => ({ ...f, mode: key }))}
                        style={{
                          flex: '1 1 150px', padding: '14px 16px', borderRadius: '10px', cursor: 'pointer', textAlign: 'left',
                          border: composeForm.mode === key ? '2px solid #3b82f6' : '1px solid var(--card-border)',
                          background: composeForm.mode === key ? 'rgba(59,130,246,0.06)' : 'transparent',
                          color: 'inherit', transition: 'all 0.2s'
                        }}>
                        <div style={{ fontWeight: 600, fontSize: '0.84rem', color: composeForm.mode === key ? '#3b82f6' : 'var(--title-color)' }}>{label}</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '2px' }}>{desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Reminder info */}
                {composeForm.mode === 'reminder' && (
                  <div style={{ padding: '14px 18px', borderRadius: '10px', background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.15)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, color: '#f59e0b', fontSize: '0.84rem', marginBottom: '6px' }}>
                      <AlertTriangle size={15} /> {noDepositUsers.length} cliente(s) sem deposito este mes
                    </div>
                    {noDepositUsers.length > 0 && (
                      <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', maxHeight: '60px', overflow: 'auto' }}>
                        {noDepositUsers.map(u => u.name.split(' ')[0]).join(', ')}
                      </div>
                    )}
                  </div>
                )}

                {/* Individual recipient */}
                {composeForm.mode === 'individual' && (
                  <div>
                    <label style={{ fontSize: '0.76rem', fontWeight: 500, color: 'var(--text-muted)', marginBottom: '8px', display: 'block' }}>Destinatario *</label>
                    <select value={composeForm.user_id} onChange={e => setComposeForm(f => ({ ...f, user_id: e.target.value }))}
                      style={{
                        width: '100%', padding: '11px 14px', borderRadius: '10px',
                        border: '1px solid var(--card-border)', background: 'var(--input-bg)',
                        color: 'var(--input-text)', fontSize: '0.84rem', outline: 'none'
                      }}>
                      <option value="">Selecione um cliente</option>
                      {usersWithEmail.map(u => (
                        <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Bulk recipients */}
                {composeForm.mode === 'bulk' && (
                  <div>
                    <label style={{ fontSize: '0.76rem', fontWeight: 500, color: 'var(--text-muted)', marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
                      <span>Destinatarios *</span>
                      <span style={{ color: '#3b82f6' }}>{composeForm.user_ids.length} selecionado(s)</span>
                    </label>
                    <div style={{
                      maxHeight: '180px', overflow: 'auto', borderRadius: '10px',
                      border: '1px solid var(--card-border)', background: 'var(--input-bg)', padding: '8px'
                    }}>
                      <label style={{
                        display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px',
                        fontSize: '0.8rem', fontWeight: 600, borderBottom: '1px solid var(--card-border)',
                        marginBottom: '4px', cursor: 'pointer', color: 'var(--title-color)'
                      }}>
                        <input type="checkbox"
                          checked={composeForm.user_ids.length === usersWithEmail.length && usersWithEmail.length > 0}
                          onChange={e => {
                            if (e.target.checked) setComposeForm(f => ({ ...f, user_ids: usersWithEmail.map(u => u.id) }));
                            else setComposeForm(f => ({ ...f, user_ids: [] }));
                          }} /> Selecionar todos ({usersWithEmail.length})
                      </label>
                      {usersWithEmail.map(u => (
                        <label key={u.id} style={{
                          display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px',
                          fontSize: '0.78rem', cursor: 'pointer', color: 'var(--text-color)',
                          borderRadius: '6px', transition: 'background 0.15s'
                        }}>
                          <input type="checkbox" checked={composeForm.user_ids.includes(u.id)}
                            onChange={e => {
                              if (e.target.checked) setComposeForm(f => ({ ...f, user_ids: [...f.user_ids, u.id] }));
                              else setComposeForm(f => ({ ...f, user_ids: f.user_ids.filter(id => id !== u.id) }));
                            }} />
                          {u.name} <span style={{ color: 'var(--text-muted)' }}>({u.email})</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Subject */}
                <div>
                  <label style={{ fontSize: '0.76rem', fontWeight: 500, color: 'var(--text-muted)', marginBottom: '8px', display: 'block' }}>
                    Assunto {composeForm.mode !== 'reminder' && '*'}
                  </label>
                  <input type="text" placeholder="Assunto do e-mail"
                    value={composeForm.subject} onChange={e => setComposeForm(f => ({ ...f, subject: e.target.value }))}
                    style={{
                      width: '100%', padding: '11px 14px', borderRadius: '10px',
                      border: '1px solid var(--card-border)', background: 'var(--input-bg)',
                      color: 'var(--input-text)', fontSize: '0.84rem', outline: 'none'
                    }}
                  />
                </div>

                {/* Body */}
                <div>
                  <label style={{ fontSize: '0.76rem', fontWeight: 500, color: 'var(--text-muted)', marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
                    <span>Corpo do e-mail {composeForm.mode !== 'reminder' && '*'}</span>
                    <span style={{ fontWeight: 400 }}>Use {'{{nome}}'} para o nome do cliente</span>
                  </label>
                  <textarea
                    rows={10} placeholder="Conteudo HTML do e-mail..."
                    value={composeForm.body_html} onChange={e => setComposeForm(f => ({ ...f, body_html: e.target.value }))}
                    style={{
                      width: '100%', padding: '14px', borderRadius: '10px', resize: 'vertical',
                      border: '1px solid var(--card-border)', background: 'var(--input-bg)',
                      color: 'var(--input-text)', fontSize: '0.82rem', outline: 'none',
                      fontFamily: 'monospace', lineHeight: '1.6', minHeight: '200px'
                    }}
                  />
                </div>

                {/* Send button */}
                <button onClick={handleSend} disabled={loading}
                  style={{
                    padding: '14px 24px', borderRadius: '10px', border: 'none',
                    background: 'linear-gradient(135deg, #2563eb, #3b82f6)', color: '#fff',
                    fontSize: '0.88rem', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s'
                  }}>
                  {loading ? <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={16} />}
                  {composeForm.mode === 'reminder' ? `Enviar lembrete para ${noDepositUsers.length} cliente(s)` : 'Enviar e-mail'}
                </button>
              </div>

              {/* Sidebar - Templates & Tips */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Template selector */}
                <div style={{
                  background: 'var(--card-bg)', borderRadius: '14px', border: '1px solid var(--card-border)',
                  padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px'
                }}>
                  <h4 style={{ margin: 0, fontSize: '0.88rem', fontWeight: 600, color: 'var(--title-color)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FileText size={15} /> Templates
                  </h4>
                  <select value={composeForm.template_id || ''}
                    onChange={e => {
                      const tpl = emailTemplates.find(t => t.id === parseInt(e.target.value));
                      if (tpl) setComposeForm(f => ({ ...f, template_id: tpl.id, subject: tpl.subject, body_html: tpl.body_html }));
                      else setComposeForm(f => ({ ...f, template_id: null }));
                    }}
                    style={{
                      width: '100%', padding: '10px 12px', borderRadius: '8px',
                      border: '1px solid var(--card-border)', background: 'var(--input-bg)',
                      color: 'var(--input-text)', fontSize: '0.82rem', outline: 'none'
                    }}>
                    <option value="">Personalizado</option>
                    {emailTemplates.map(t => <option key={t.id} value={t.id}>{t.name} ({t.category})</option>)}
                  </select>
                </div>

                {/* Variables help */}
                <div style={{
                  background: 'var(--card-bg)', borderRadius: '14px', border: '1px solid var(--card-border)',
                  padding: '20px', display: 'flex', flexDirection: 'column', gap: '10px'
                }}>
                  <h4 style={{ margin: 0, fontSize: '0.88rem', fontWeight: 600, color: 'var(--title-color)' }}>Variaveis disponiveis</h4>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-color)', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div style={{ padding: '8px 10px', borderRadius: '6px', background: 'var(--input-bg)', fontFamily: 'monospace' }}>
                      <span style={{ color: '#3b82f6' }}>{'{{nome}}'}</span> <span style={{ color: 'var(--text-muted)' }}>- Nome do cliente</span>
                    </div>
                    <div style={{ padding: '8px 10px', borderRadius: '6px', background: 'var(--input-bg)', fontFamily: 'monospace' }}>
                      <span style={{ color: '#3b82f6' }}>{'{{email}}'}</span> <span style={{ color: 'var(--text-muted)' }}>- E-mail do cliente</span>
                    </div>
                  </div>
                </div>

                {/* Tracking info */}
                <div style={{
                  background: 'rgba(59,130,246,0.04)', borderRadius: '14px', border: '1px solid rgba(59,130,246,0.1)',
                  padding: '20px', display: 'flex', flexDirection: 'column', gap: '8px'
                }}>
                  <h4 style={{ margin: 0, fontSize: '0.84rem', fontWeight: 600, color: '#3b82f6', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <BarChart3 size={14} /> Rastreamento automatico
                  </h4>
                  <p style={{ margin: 0, fontSize: '0.76rem', color: 'var(--text-color)', lineHeight: '1.5' }}>
                    Todos os e-mails enviados possuem rastreamento de abertura e cliques automatico. Links no corpo do e-mail sao rastreados.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ================================ */}
          {/* TEMPLATES SECTION                */}
          {/* ================================ */}
          {section === 'templates' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              {/* Template Form */}
              <div style={{
                background: 'var(--card-bg)', borderRadius: '14px', border: '1px solid var(--card-border)',
                padding: '28px', display: 'flex', flexDirection: 'column', gap: '16px'
              }}>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: 'var(--title-color)' }}>
                  {templateForm.editing ? 'Editar template' : 'Novo template'}
                </h3>

                <div>
                  <label style={{ fontSize: '0.76rem', fontWeight: 500, color: 'var(--text-muted)', marginBottom: '6px', display: 'block' }}>Nome *</label>
                  <input type="text" placeholder="Nome do template"
                    value={templateForm.name} onChange={e => setTemplateForm(f => ({ ...f, name: e.target.value }))}
                    style={{
                      width: '100%', padding: '10px 14px', borderRadius: '10px',
                      border: '1px solid var(--card-border)', background: 'var(--input-bg)',
                      color: 'var(--input-text)', fontSize: '0.84rem', outline: 'none'
                    }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '0.76rem', fontWeight: 500, color: 'var(--text-muted)', marginBottom: '6px', display: 'block' }}>Categoria</label>
                  <select value={templateForm.category} onChange={e => setTemplateForm(f => ({ ...f, category: e.target.value }))}
                    style={{
                      width: '100%', padding: '10px 14px', borderRadius: '10px',
                      border: '1px solid var(--card-border)', background: 'var(--input-bg)',
                      color: 'var(--input-text)', fontSize: '0.84rem', outline: 'none'
                    }}>
                    <option value="geral">Geral</option>
                    <option value="lembrete">Lembrete</option>
                    <option value="cobranca">Cobranca</option>
                    <option value="informativo">Informativo</option>
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: '0.76rem', fontWeight: 500, color: 'var(--text-muted)', marginBottom: '6px', display: 'block' }}>Assunto *</label>
                  <input type="text" placeholder="Assunto do template"
                    value={templateForm.subject} onChange={e => setTemplateForm(f => ({ ...f, subject: e.target.value }))}
                    style={{
                      width: '100%', padding: '10px 14px', borderRadius: '10px',
                      border: '1px solid var(--card-border)', background: 'var(--input-bg)',
                      color: 'var(--input-text)', fontSize: '0.84rem', outline: 'none'
                    }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '0.76rem', fontWeight: 500, color: 'var(--text-muted)', marginBottom: '6px', display: 'block' }}>Corpo HTML *</label>
                  <textarea rows={8} placeholder="Conteudo HTML do template..."
                    value={templateForm.body_html} onChange={e => setTemplateForm(f => ({ ...f, body_html: e.target.value }))}
                    style={{
                      width: '100%', padding: '14px', borderRadius: '10px', resize: 'vertical',
                      border: '1px solid var(--card-border)', background: 'var(--input-bg)',
                      color: 'var(--input-text)', fontSize: '0.82rem', outline: 'none',
                      fontFamily: 'monospace', lineHeight: '1.6'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={handleSaveTemplate} disabled={loading}
                    style={{
                      flex: 1, padding: '12px', borderRadius: '10px', border: 'none',
                      background: 'linear-gradient(135deg, #2563eb, #3b82f6)', color: '#fff',
                      fontSize: '0.84rem', fontWeight: 600, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                    }}>
                    <Check size={15} /> Salvar
                  </button>
                  {templateForm.editing && (
                    <button onClick={() => setTemplateForm({ name: '', subject: '', body_html: '', category: 'geral', editing: null })}
                      style={{
                        padding: '12px 20px', borderRadius: '10px',
                        border: '1px solid var(--card-border)', background: 'transparent',
                        color: 'var(--text-color)', fontSize: '0.84rem', cursor: 'pointer'
                      }}>Cancelar</button>
                  )}
                </div>
              </div>

              {/* Template List */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <h3 style={{ margin: '0 0 4px', fontSize: '1rem', fontWeight: 600, color: 'var(--title-color)' }}>
                  Templates salvos ({emailTemplates.length})
                </h3>

                {emailTemplates.length === 0 ? (
                  <div style={{
                    background: 'var(--card-bg)', borderRadius: '14px', border: '1px solid var(--card-border)',
                    padding: '48px 20px', textAlign: 'center', color: 'var(--text-muted)'
                  }}>
                    <FileText size={32} style={{ opacity: 0.3, marginBottom: '12px' }} />
                    <p style={{ fontSize: '0.85rem' }}>Nenhum template criado</p>
                  </div>
                ) : emailTemplates.map(t => (
                  <div key={t.id} style={{
                    background: 'var(--card-bg)', borderRadius: '12px', border: '1px solid var(--card-border)',
                    padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '8px',
                    transition: 'border-color 0.2s'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontWeight: 600, fontSize: '0.88rem', color: 'var(--title-color)' }}>{t.name}</span>
                        <span style={{
                          padding: '3px 10px', borderRadius: '6px', fontSize: '0.68rem', fontWeight: 500,
                          background: 'rgba(59,130,246,0.08)', color: '#3b82f6'
                        }}>{t.category}</span>
                      </div>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button onClick={() => setTemplateForm({ name: t.name, subject: t.subject, body_html: t.body_html, category: t.category, editing: t.id })}
                          style={{ padding: '6px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'transparent', cursor: 'pointer', color: 'var(--text-color)' }}>
                          <Edit3 size={14} />
                        </button>
                        <button onClick={() => handleDeleteTemplate(t.id)}
                          style={{ padding: '6px', borderRadius: '6px', border: '1px solid rgba(239,68,68,0.2)', background: 'transparent', cursor: 'pointer', color: '#ef4444' }}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{t.subject}</div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', opacity: 0.7, maxHeight: '40px', overflow: 'hidden' }}>
                      {t.body_html.replace(/<[^>]*>/g, '').slice(0, 120)}...
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ================================ */}
          {/* STATS SECTION                    */}
          {/* ================================ */}
          {section === 'stats' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {!emailStats ? (
                <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-muted)' }}>
                  <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite', marginBottom: '12px' }} />
                  <p>Carregando estatisticas...</p>
                </div>
              ) : (
                <>
                  {/* Main KPIs */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                    {[
                      { label: 'Total de e-mails', value: emailStats.summary.total, subtitle: 'Todos os tempos', color: 'var(--title-color)', bg: 'var(--card-bg)' },
                      { label: 'Taxa de abertura', value: `${emailStats.summary.open_rate || 0}%`, subtitle: `${parseInt(emailStats.summary.opened || 0) + parseInt(emailStats.summary.clicked || 0)} abertos`, color: '#22c55e', bg: 'rgba(34,197,94,0.04)' },
                      { label: 'Taxa de cliques', value: `${emailStats.summary.click_rate || 0}%`, subtitle: `${emailStats.summary.clicked || 0} clicaram`, color: '#3b82f6', bg: 'rgba(59,130,246,0.04)' },
                      { label: 'Falhas', value: emailStats.summary.failed, subtitle: 'E-mails nao entregues', color: '#ef4444', bg: 'rgba(239,68,68,0.04)' },
                    ].map((item, i) => (
                      <div key={i} style={{
                        padding: '24px', borderRadius: '14px',
                        background: item.bg, border: '1px solid var(--card-border)'
                      }}>
                        <div style={{ fontSize: '0.78rem', fontWeight: 500, color: 'var(--text-muted)', marginBottom: '8px' }}>{item.label}</div>
                        <div style={{ fontSize: '2rem', fontWeight: 700, color: item.color, marginBottom: '4px' }}>{item.value}</div>
                        <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>{item.subtitle}</div>
                      </div>
                    ))}
                  </div>

                  {/* Daily chart + Top links */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    {/* Daily breakdown */}
                    <div style={{
                      background: 'var(--card-bg)', borderRadius: '14px', border: '1px solid var(--card-border)',
                      padding: '24px', display: 'flex', flexDirection: 'column', gap: '14px'
                    }}>
                      <h3 style={{ margin: 0, fontSize: '0.92rem', fontWeight: 600, color: 'var(--title-color)' }}>Ultimos 7 dias</h3>
                      {emailStats.daily.length === 0 ? (
                        <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem', textAlign: 'center', padding: '20px 0' }}>Sem dados recentes</p>
                      ) : emailStats.daily.map((day, i) => {
                        const maxTotal = Math.max(...emailStats.daily.map(d => parseInt(d.total) || 1));
                        return (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                            <span style={{ minWidth: '50px', fontSize: '0.78rem', fontWeight: 500, color: 'var(--text-color)' }}>
                              {new Date(day.date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                            </span>
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '3px' }}>
                              {/* Bar */}
                              <div style={{ height: '20px', borderRadius: '4px', background: 'var(--progress-bg)', overflow: 'hidden', position: 'relative' }}>
                                <div style={{
                                  position: 'absolute', left: 0, top: 0, bottom: 0,
                                  width: `${(parseInt(day.total) / maxTotal * 100)}%`,
                                  background: 'linear-gradient(90deg, #2563eb, #3b82f6)',
                                  borderRadius: '4px', transition: 'width 0.3s'
                                }} />
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '10px', fontSize: '0.72rem', minWidth: '160px' }}>
                              <span style={{ color: 'var(--text-color)' }}>{day.total} env.</span>
                              <span style={{ color: '#22c55e' }}>{day.opens} ab.</span>
                              <span style={{ color: '#3b82f6' }}>{day.clicks} cl.</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Top links */}
                    <div style={{
                      background: 'var(--card-bg)', borderRadius: '14px', border: '1px solid var(--card-border)',
                      padding: '24px', display: 'flex', flexDirection: 'column', gap: '14px'
                    }}>
                      <h3 style={{ margin: 0, fontSize: '0.92rem', fontWeight: 600, color: 'var(--title-color)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <MousePointerClick size={16} /> Links mais clicados
                      </h3>
                      {emailStats.topLinks.length === 0 ? (
                        <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem', textAlign: 'center', padding: '20px 0' }}>Nenhum clique registrado</p>
                      ) : emailStats.topLinks.map((link, i) => (
                        <div key={i} style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '12px 14px', borderRadius: '10px', border: '1px solid var(--card-border)',
                          gap: '12px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', overflow: 'hidden', flex: 1 }}>
                            <span style={{
                              width: '24px', height: '24px', borderRadius: '6px',
                              background: 'rgba(59,130,246,0.1)', color: '#3b82f6',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '0.72rem', fontWeight: 700, flexShrink: 0
                            }}>{i + 1}</span>
                            <span style={{ fontSize: '0.78rem', color: 'var(--text-color)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {link.original_url}
                            </span>
                          </div>
                          <span style={{ fontWeight: 700, color: '#3b82f6', fontSize: '0.84rem', whiteSpace: 'nowrap' }}>
                            {link.clicks}x
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

        </main>
      </div>

      {/* Spin animation for loading */}
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

export default EmailPage;
